import { createFileRoute } from "@tanstack/react-router";
import { Heart, Award, CheckCircle2 } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { ContentBlock } from "@/components/ContentBlock";
import { CtaBanner } from "@/components/CtaBanner";
import { RelatedLinks } from "@/components/RelatedLinks";
import { Reveal } from "@/components/Reveal";
import { FaqSection, faqJsonLd, type FaqItem } from "@/components/FaqSection";
import { IMAGES } from "@/lib/images";
import { PostHeroConversion } from "@/components/sections/PostHeroConversion";

export const FAQS: FaqItem[] = [
  {
    q: "Hur länge har BBN Nordic funnits?",
    a: "Vi grundade BBN Nordic AB 2008 och hade då redan många års erfarenhet av fönsterfilm. I dag har vi 3 500+ nöjda kunder bland fastighetsägare, butiker, kontor och privatpersoner i hela Sverige.",
  },
  {
    q: "Vad innebär det att ni är auktoriserade 3M-installatörer?",
    a: "Endast auktoriserade installatörer får montera 3M:s serier Prestige och Ultra och utfärda 3M:s fulla produktgaranti. Auktorisationen kräver löpande utbildning, dokumenterade installationsrutiner och inspektion från 3M. Vi har varit auktoriserade i mer än ett decennium.",
  },
  {
    q: "Har ni egen montagepersonal eller anlitar ni underentreprenörer?",
    a: "Alla våra montörer är anställda av BBN Nordic. Vi använder aldrig okvalificerade underentreprenörer för installation, utan följer 3M:s installationsprotokoll med egen personal för att kunna ansvara för montering och garanti.",
  },
  {
    q: "Hur stöttar ni Sofias Änglar?",
    a: "Vi har sponsrat tv-programmet Sofias Änglar med kostnadsfri säkerhetsfilm och solfilm till familjer som behövde tryggare och mer funktionella hem. Vi fortsätter också att avsätta en del av vår kapacitet varje år till välgörenhetsprojekt.",
  },
  {
    q: "Vilka är era främsta uppdragsgivare?",
    a: "Vi arbetar med både fastighetsbolag, kommuner, butikskedjor, advokat- och revisionsfirmor, vårdcentraler, skolor, hotell, restauranger och privatpersoner.",
  },
];

export const Route = createFileRoute("/om-oss")({
  head: () => ({
    meta: [
      { title: "Om BBN Nordic — Auktoriserad 3M-installatör sedan 2008" },
      { name: "description", content: "Auktoriserad 3M- och Solar Screen-installatör sedan 2008. Stolt sponsor av Sofias Änglar." },
      { property: "og:title", content: "Om BBN Nordic" },
      { property: "og:description", content: "Officiella auktoriserade 3M-installatörer i Sverige — sedan 2008. Vi stöttar också Sofias Änglar." },
      { property: "og:url", content: "https://bbnnordic.se/om-oss" },
      { property: "og:image", content: IMAGES.installer },
      { property: "og:type", content: "article" },
    ],
    links: [{ rel: "canonical", href: "https://bbnnordic.se/om-oss" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(faqJsonLd(FAQS)),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AboutPage",
          "@id": "https://bbnnordic.se/om-oss#aboutpage",
          name: "Om BBN Nordic",
          url: "https://bbnnordic.se/om-oss",
          mainEntity: { "@id": "https://bbnnordic.se/#organization" },
        }),
      },
    ],
  }),
  component: OmOssPage,
});

function OmOssPage() {
  return (
    <>
      <PageHero
        eyebrow="OM OSS"
        title="En pålitlig partner sedan 2008"
        subtitle="Vi är officiella auktoriserade 3M-installatörer i Sverige och återförsäljare av Solar Screen."
        image={IMAGES.installer}
        breadcrumbs={[{ label: "Om oss" }]}
      />
      <PostHeroConversion />




      <ContentBlock
        eyebrow="VÅR HISTORIA"
        heading="Vi har arbetat med fönsterfilm sedan 2008."
        paragraphs={[
          "Vi startade BBN Nordic AB 2008 efter många år som montörer av fönsterfilm. Sedan dess har vi fått 3 500+ nöjda kunder bland fastighetsägare, butiker, kontor, skolor, vårdcentraler och privatpersoner i hela Sverige.",
          "Vi är fortfarande ett ägarlett, familjärt företag. Vi väljer film efter glaset, monterar själva och ansvarar för arbetet även efteråt.",
        ]}
        bullets={[
          "Grundat 2008 — auktoriserad 3M-installatör i över ett decennium",
          "Återförsäljare av Solar Screen (Luxemburg)",
          "Bas i Staffanstorp — verksamma i hela Sverige",
          "Egen montagepersonal — vi anlitar inga okvalificerade underentreprenörer",
        ]}
        image={IMAGES.installer}
      />

      {/* Vad vi tror på */}
      <ContentBlock
        eyebrow="VÅR FILOSOFI"
        heading="Så väljer vi material och uppdrag."
        paragraphs={[
          "Vi arbetar med 3M, Solar Screen och NoTrax, tillverkare vi själva skulle välja. Vi privatimporterar inte okända varumärken. Materialet ska passa uppdraget och garantin ska vara en vi kan stå för.",
          "Ibland behöver vi säga nej, till exempel när en film inte passar glaset eller när tidsplanen inte räcker för en ordentlig installation. Vi tackar också nej om priset kräver material vi inte tror på. Så har vi arbetat sedan 2008.",
        ]}
        bullets={[
          "Endast auktoriserade material från 3M, Solar Screen och NoTrax",
          "Egen, fast anställd montagepersonal — ingen underentreprenörsdrift",
          "Glasanalys före varje installation — så att glasgaranti bibehålls",
          "Skriftlig garanti och installationsprotokoll på varje uppdrag",
        ]}
        image={IMAGES.solfilm}
      />

      {/* Auktoriserad installatör */}
      <section className="bg-navy-light py-20 md:py-28 border-y border-border">
        <div className="mx-auto max-w-7xl px-5 md:px-10">
          <Reveal>
            <p className="section-number">CERTIFIERING</p>
            <h2 className="mt-3 text-3xl md:text-4xl max-w-2xl">
              Auktoriserad 3M-installatör — vad innebär det?
            </h2>
          </Reveal>
          <div className="mt-10 grid md:grid-cols-3 gap-5">
            {[
              { icon: Award, title: "Officiellt 3M-certifierad", desc: "Endast auktoriserade installatörer får montera 3M:s premiumserier och utfärda fullgaranti." },
              { icon: CheckCircle2, title: "10–15 års garanti", desc: "Vi lämnar full produkt- och installationsgaranti, försäkrad genom 3M." },
              { icon: CheckCircle2, title: "Löpande utbildning", desc: "Våra montörer går årliga kurser i nya produkter, monteringsteknik och säkerhetskrav." },
            ].map(({ icon: Icon, title, desc }) => (
              <Reveal key={title}>
                <div className="glass p-7 h-full">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded bg-gold/15 text-gold">
                    <Icon size={20} />
                  </span>
                  <h3 className="mt-5 text-lg font-semibold">{title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground font-light">{desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Sofias Änglar */}
      <section className="py-20 md:py-28 bg-bbn-tint border-y border-border">
        <div className="mx-auto max-w-7xl px-5 md:px-10">
          <div className="grid md:grid-cols-[1fr_auto] gap-10 md:gap-16 items-center">
            <div>
              <Reveal>
                <span className="inline-flex items-center gap-2 text-sm font-semibold text-bbn-accent uppercase tracking-wider">
                  <Heart size={16} className="fill-bbn-accent/20 text-bbn-accent" />
                  Vårt engagemang
                </span>
              </Reveal>

              <Reveal delay={0.05}>
                <h2 className="mt-4 text-3xl md:text-4xl lg:text-5xl leading-tight">
                  Stolt sponsor av Sofias Änglar
                </h2>
              </Reveal>

              <Reveal delay={0.1}>
                <p className="mt-3 text-lg md:text-xl text-muted-foreground font-light">
                  Vi har bidragit med solfilm och säkerhetsfilm
                </p>
              </Reveal>

              <Reveal delay={0.15}>
                <div className="mt-6 space-y-4 text-muted-foreground font-light leading-relaxed max-w-2xl">
                  <p>
                    Genom Sofias Änglar har vi fått hjälpa familjer som behövde tryggare och mer funktionella hem. Vi bidrog med det vi kan: fönsterfilm och skyddslösningar. Det är ett sätt för oss att använda vår erfarenhet även utanför våra vanliga kunduppdrag.
                  </p>
                </div>
              </Reveal>

            </div>

            <Reveal delay={0.15}>
              <div className="relative flex items-center justify-center">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-48 h-48 md:w-64 md:h-64 rounded-full border border-bbn-accent/10" />
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-36 h-36 md:w-48 md:h-48 rounded-full border border-bbn-accent/20" />
                </div>
                <div className="relative z-10 flex flex-col items-center justify-center w-32 h-32 md:w-40 md:h-40 rounded-full bg-bbn-primary text-white shadow-lg">
                  <Heart size={32} className="fill-white/20" />
                  <span className="mt-2 text-[10px] md:text-xs font-bold uppercase tracking-[0.2em] text-center leading-tight">
                    Sofias<br />Änglar
                  </span>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <FaqSection
        heading="Vanliga frågor om BBN Nordic."
        items={FAQS}
      />

      <CtaBanner />

      <RelatedLinks
        items={[
          { to: "/radgivning", label: "Rådgivning & service", desc: "Vi hjälper dig välja rätt film." },
          { to: "/referenser", label: "Referenser", desc: "Se utförda projekt." },
          { to: "/kontakt", label: "Kontakt", desc: "Ring eller mejla oss om ditt projekt." },
        ]}
      />

    </>
  );
}
